/* global assignToVenuesResetUrlTemplate csrfToken */
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';

import moment from 'moment';

import { PacksDispatchContext, PacksContext } from '../ApiProvider';
import { AssignContext } from '../AssignProvider';

import { saveAssigns } from 'Helpers/costPacks';

import Button from 'CommonComponents/Button';
import Tooltip from 'CommonComponents/Tooltip';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import CircleInfoIcon from 'Assets/icons/circle-info.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const timeToFloat = (time) => {
    const hoursMinutes = time.toString().split(':');
    const hours = parseInt(hoursMinutes[0], 10);
    const minutes = hoursMinutes[1] ? parseInt(hoursMinutes[1], 10) : 0;
    return hours + minutes / 60;
};

// Helper for edit mode calculations: convert "HH:mm" to decimal hours
function parseTimeToHours(timeStr) {
    if (!timeStr || timeStr === '-') return 0;
    return timeToFloat(timeStr);
}

/** Hours from start to end; if end is earlier on the clock than start, treat as crossing midnight (24h wrap). */
function shiftHoursBetween(startStr, endStr) {
    const start = parseTimeToHours(startStr);
    const end = parseTimeToHours(endStr);
    let diff = end - start;
    if (diff < 0) {
        diff += 24;
    }
    return Math.max(0, diff);
}

const findShifts = (duration, price, max_shift_duration, min_shift_duration, shift_overlap, shifts = 0, array = []) => {
    if (max_shift_duration === 0) {
        return array;
    }

    shifts++;
    duration = shifts > 1 ? duration + shift_overlap : duration;
    let hours = 0;
    let total = 0;

    if (duration >= max_shift_duration) {
        hours = max_shift_duration;
    } else if (duration >= min_shift_duration) {
        hours = duration;
    } else {
        hours = min_shift_duration;
    }

    total += price * hours;
    array.push({ name: `Quantity shift ${shifts}`, hours: hours, total: total });
    duration -= hours;

    if (duration > 0) {
        findShifts(duration, price, max_shift_duration, min_shift_duration, shift_overlap, shifts, array);
    }

    return array;
};

function computeShiftStartEnd(detail, shiftArray, item, dateForDetail) {
    if (!detail || !('start' in detail) || !shiftArray?.length) {
        return shiftArray.map(() => ({ start: '-', end: '-' }));
    }

    let startTimeBefore = item.start_time_before;
    let startMoment = null;

    if (moment.isMoment(detail.start)) {
        startMoment = detail.start.clone();
    } else if (detail.start) {
        startMoment = moment(detail.start);
        if (!startMoment.isValid()) {
            const timeStr = typeof detail.start === 'string' ? detail.start.trim() : String(detail.start);
            if (/^\d{1,2}:\d{2}(:\d{2})?(\s*[aApP][mM])?$/i.test(timeStr) && dateForDetail) {
                startMoment = moment(`${dateForDetail} ${timeStr}`, [
                    'YYYY-MM-DD HH:mm:ss',
                    'YYYY-MM-DD HH:mm',
                    'YYYY-MM-DD hh:mm:ss A',
                    'YYYY-MM-DD hh:mm A',
                    'YYYY-MM-DD h:mm:ss A',
                    'YYYY-MM-DD h:mm A',
                ]);
            } else {
                startMoment = moment(
                    detail.start,
                    [
                        'HH:mm',
                        'H:mm',
                        'HH:mm:ss',
                        'hh:mm A',
                        'h:mm A',
                        'hh:mm:ss A',
                        'h:mm:ss A',
                        'YYYY-MM-DD HH:mm',
                        'YYYY-MM-DD HH:mm:ss',
                        'YYYY-MM-DD hh:mm A',
                        'YYYY-MM-DD h:mm A',
                        'YYYY-MM-DDTHH:mm:ss',
                        'YYYY-MM-DDTHH:mm:ss.SSSZ',
                        moment.ISO_8601,
                    ],
                    true,
                );
            }
        }
    }

    if (!startMoment || !startMoment.isValid()) {
        return shiftArray.map(() => ({ start: '-', end: '-' }));
    }

    const result = [];
    let currentStart = startMoment.clone();

    for (let i = 0; i < shiftArray.length; i++) {
        const hours = shiftArray[i].hours;

        if (detail && detail.useSessionTime) {
            currentStart = i === 0 ? currentStart.clone().subtract(startTimeBefore, 'hour') : currentStart;
        }

        let endMoment = currentStart.clone().add(hours, 'hours');

        result.push({
            start: currentStart.format('HH:mm'),
            end: endMoment.format('HH:mm'),
        });

        currentStart = endMoment.clone().subtract(parseFloat(item.shift_overlap) || 0, 'hours');
    }

    return result;
}

function normalizeShiftMap(shiftMap = {}) {
    const normalized = {};
    [1, 2, 3].forEach((idx) => {
        normalized[idx] = {
            start: shiftMap?.[idx]?.start ?? '-',
            end: shiftMap?.[idx]?.end ?? '-',
        };
    });
    return normalized;
}

function mergeShiftMaps(baseShiftMap = {}, editedShiftMap = {}) {
    const merged = {};
    [1, 2, 3].forEach((idx) => {
        merged[idx] = {
            start: editedShiftMap?.[idx]?.start !== undefined ? editedShiftMap[idx].start : (baseShiftMap?.[idx]?.start ?? '-'),
            end: editedShiftMap?.[idx]?.end !== undefined ? editedShiftMap[idx].end : (baseShiftMap?.[idx]?.end ?? '-'),
        };
    });
    return merged;
}

function durationFromShiftMap(shiftMap = {}) {
    return [1, 2, 3].reduce((sum, idx) => {
        const shift = shiftMap[idx] || {};
        if (!shift.start || !shift.end || shift.start === '-' || shift.end === '-') {
            return sum;
        }
        return sum + shiftHoursBetween(shift.start, shift.end);
    }, 0);
}

function durationFromCalculatedArray(calculated) {
    if (!calculated || !Array.isArray(calculated.array)) {
        return 0;
    }
    return calculated.array.reduce((sum, row) => sum + (row?.hours || 0), 0);
}

function calculateItemDetails(item, details, selectedSchedules) {
    let total = 0;
    let array = [
        {
            name: 'Quantity shift 1',
            hours: 0,
            total: 0,
        },
    ];

    if (selectedSchedules && item.pack_contractor) {
        let duration = details && 'duration' in details ? details['duration'] : timeToFloat(selectedSchedules.duration);

        const useSessionTime = 'useSessionTime' in details ? details.useSessionTime : true;
        duration += useSessionTime ? item.start_time_before + item.finish_time_after : 0;

        if (duration > item.max_shift_duration) {
            array = findShifts(duration, item.price, item.max_shift_duration, item.min_shift_duration, item.shift_overlap);
            for (let i = 0; i < array.length; i++) {
                total += array[i]['total'];
            }
        } else {
            if (duration <= item.max_shift_duration && duration >= item.min_shift_duration) {
                total = duration.toFixed(2) * item.price;
            } else if (duration < item.min_shift_duration) {
                total = item.price * item.min_shift_duration;
            }

            array[0].hours = duration;
            array[0].total = total;
        }
    }

    return { array, total };
}

const GAP_PX = 16;
const TABLE_RIGHT_PADDING_PX = 16;
const ACTIONS_COL_PX = 36;

function getGridStyle(expandedShift1, expandedShift2, expandedShift3, containerWidth, isEditMode = false) {
    const w = containerWidth || 1200;
    // 8 base columns (desc, rate, qty1, qty2, qty3, people, total, actions) → 7 gaps
    const numGapsCollapsed = 7;
    const availableWidth = w - numGapsCollapsed * GAP_PX - TABLE_RIGHT_PADDING_PX - ACTIONS_COL_PX;
    const unit = availableWidth / 7;
    const descWidth = 2 * unit;
    const otherColWidth = (5 * unit) / 6;
    const baseStartEndWidth = otherColWidth / 2;
    const startEndWidth = isEditMode ? Math.max(baseStartEndWidth, 145) : baseStartEndWidth;
    const parts = [
        descWidth,
        otherColWidth,
        otherColWidth,
        ...(expandedShift1 ? [startEndWidth, startEndWidth] : []),
        otherColWidth,
        ...(expandedShift2 ? [startEndWidth, startEndWidth] : []),
        otherColWidth,
        ...(expandedShift3 ? [startEndWidth, startEndWidth] : []),
        otherColWidth,
        otherColWidth,
        ACTIONS_COL_PX,
    ];
    return { gridTemplateColumns: parts.map((p) => `${p}px`).join(' ') };
}

function getTableWidthStyle(expandedShift1, expandedShift2, expandedShift3, containerWidth, isEditMode = false) {
    if (!containerWidth) return { width: '100%' };
    const expandedCount = (expandedShift1 ? 1 : 0) + (expandedShift2 ? 1 : 0) + (expandedShift3 ? 1 : 0);
    if (expandedCount === 0) return { width: '100%' };
    const numGapsCollapsed = 7;
    const availableWidth = containerWidth - numGapsCollapsed * GAP_PX - TABLE_RIGHT_PADDING_PX - ACTIONS_COL_PX;
    const unit = availableWidth / 7;
    const descWidth = 2 * unit;
    const otherColWidth = (5 * unit) / 6;
    const baseStartEndWidth = otherColWidth / 2;
    const startEndWidth = isEditMode ? Math.max(baseStartEndWidth, 145) : baseStartEndWidth;
    const numStartEndCols = expandedCount * 2;
    const numColsExpanded = 8 + numStartEndCols;
    const numGapsExpanded = numColsExpanded - 1;
    const totalWidth = descWidth + 6 * otherColWidth + numStartEndCols * startEndWidth + ACTIONS_COL_PX + numGapsExpanded * GAP_PX + TABLE_RIGHT_PADDING_PX;
    return { minWidth: `${Math.ceil(totalWidth)}px` };
}

export default function EditContractorPackToVenueForm({ packId, venueId, filterDay, filterDays, formattedDate, formattedDates, selectedSchedules }) {
    const { packs, venues, contractorPackItems } = useContext(PacksContext);
    const { filteredPacks } = useContext(AssignContext);
    const apiCall = useContext(PacksDispatchContext);

    const assigned = filteredPacks.find((p) => p.packId == packId);
    const details = assigned && Object.prototype.hasOwnProperty.call(assigned, 'details') ? assigned.details : null;
    const allDetails = assigned && Object.prototype.hasOwnProperty.call(assigned, 'allDetails') ? assigned.allDetails : [];
    const allCustomizations = assigned && Object.prototype.hasOwnProperty.call(assigned, 'allCustomizations') ? assigned.allCustomizations : [];

    const [pack, setPack] = useState(null);
    const [detailsState, setDetailsState] = useState({
        packId: false,
        venueId: false,
        packName: '',
        venueName: '',
        formattedDate: '',
        amount: 1,
        filterDay: '',
        filterDays: [],
        packContractor: true,
        details: details,
    });
    const [checked, setChecked] = useState(false);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [expandedShift1, setExpandedShift1] = useState(false);
    const [expandedShift2, setExpandedShift2] = useState(false);
    const [expandedShift3, setExpandedShift3] = useState(false);
    const [allRows, setAllRows] = useState([]);
    const instanceCounter = useRef(0);
    const [containerWidth, setContainerWidth] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedRows, setEditedRows] = useState({});
    const scrollWrapperRef = useRef(null);
    const hasInitializedCustomizationsRef = useRef(false);
    const [totalData, setTotalData] = useState({
        name: 'Total',
        total: 0,
        qty1: 0,
        qty2: 0,
        qty3: 0,
        people: 0,
    });

    useEffect(() => {
        if (!packId || !venueId) return;
        const thisPack = packs.find((p) => p.id == packId);
        const thisVenue = venues.find((v) => v.id == venueId);
        const amount = assigned && Object.prototype.hasOwnProperty.call(assigned, 'amount') ? assigned.amount : 1;
        setPack(thisPack);
        const firstDetail = allDetails && allDetails[0] ? allDetails[0] : details;
        setStartTime(firstDetail && 'start' in firstDetail ? moment(firstDetail['start']).format('HH:mm') : '');
        setEndTime(firstDetail && 'end' in firstDetail ? moment(firstDetail['end']).format('HH:mm') : '');
        setDetailsState({
            packId,
            venueId,
            packName: thisPack?.name,
            venueName: thisVenue?.name,
            formattedDate,
            formattedDates,
            filterDay,
            filterDays,
            amount,
            details: firstDetail,
            packContractor: thisPack?.pack_contractor,
        });
        if (firstDetail) {
            setChecked(!!(firstDetail.useSessionTime && 'start' in firstDetail && firstDetail.start));
        }
        hasInitializedCustomizationsRef.current = false;
    }, [packId, venueId, packs, venues, assigned, details, allDetails, formattedDate, formattedDates, filterDay, filterDays]);

    useEffect(() => {
        if (hasInitializedCustomizationsRef.current) {
            return;
        }
        if (!pack?.pack_children || !filterDays?.length) {
            return;
        }

        const nextEditedRows = {};
        const nextAllRows = [];
        instanceCounter.current = 0;

        filterDays.forEach((day, detailIndex) => {
            const dateCustomizations = allCustomizations?.[detailIndex] || {};
            const itemsArray = Array.isArray(dateCustomizations.items) && dateCustomizations.items.length > 0 ? dateCustomizations.items : null;

            const detail = allDetails?.[detailIndex] || details;
            const sched =
                _.isArray(selectedSchedules) && selectedSchedules.length > 0 ? selectedSchedules[detailIndex] || selectedSchedules[0] : selectedSchedules;

            if (itemsArray) {
                // Restore from saved items array — new format
                itemsArray.forEach((entry) => {
                    const item = contractorPackItems?.find((packItem) => packItem.id == entry.item_id);
                    if (!item) {
                        return;
                    }
                    const calculated = calculateItemDetails(item, detail, sched);
                    const instanceId = String(instanceCounter.current++);
                    nextAllRows.push({
                        instanceId,
                        item,
                        isOriginal: entry.is_original !== false,
                        date: day,
                        calculated,
                    });
                    const nextRow = {};
                    if (entry.rate_per_hour !== undefined) {
                        nextRow.ratePerHour = entry.rate_per_hour;
                    }
                    if (entry.shifts && typeof entry.shifts === 'object') {
                        nextRow.shifts = entry.shifts;
                    }
                    if (Object.keys(nextRow).length > 0) {
                        nextEditedRows[instanceId] = nextRow;
                    }
                });
            } else {
                // No customizations — new or unedited assignment: expand by pivot.amount
                const amount = assigned?.amount || 1;
                pack.pack_children.forEach((packChild) => {
                    const childAmount = (packChild.pivot?.amount || 1) * amount;
                    const calculated = calculateItemDetails(packChild, detail, sched);
                    _.range(childAmount).forEach(() => {
                        const instanceId = String(instanceCounter.current++);
                        nextAllRows.push({
                            instanceId,
                            item: packChild,
                            isOriginal: true,
                            date: day,
                            calculated,
                        });
                    });
                });
            }
        });

        setAllRows(nextAllRows);
        setEditedRows(nextEditedRows);
        hasInitializedCustomizationsRef.current = true;
    }, [pack, filterDays, allCustomizations, contractorPackItems, allDetails, selectedSchedules, details, assigned]);

    const dropdownOptions = useMemo(() => {
        if (!pack?.pack_children) return [];
        const inPackIds = pack.pack_children.map((c) => c.id);
        const packItemsFirst = [...pack.pack_children];
        const unassigned = (contractorPackItems || []).filter((c) => !inPackIds.includes(c.id)).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        return [...packItemsFirst, ...unassigned];
    }, [pack, contractorPackItems]);

    const getDetailForCalculation = useCallback(() => {
        const firstDetail = allDetails && allDetails[0] ? allDetails[0] : details;
        return firstDetail;
    }, [allDetails, details]);

    const getDetailForDate = useCallback(
        (dateForDetail) => {
            if (!dateForDetail || !filterDays?.length) {
                return getDetailForCalculation();
            }
            const index = filterDays.findIndex((day) => day === dateForDetail);
            if (index >= 0 && allDetails?.[index]) {
                return allDetails[index];
            }
            return getDetailForCalculation();
        },
        [filterDays, allDetails, getDetailForCalculation],
    );

    const addRowForItem = useCallback(
        (item) => {
            const instanceId = String(instanceCounter.current++);
            const rowDate = detailsState.filterDay;
            const detail = getDetailForCalculation();
            const sched = _.isArray(selectedSchedules) && selectedSchedules.length > 0 ? selectedSchedules[0] : selectedSchedules;
            const calculated = calculateItemDetails(item, detail, sched);
            setAllRows((prev) => [...prev, { instanceId, item, isOriginal: false, date: rowDate, calculated }]);
        },
        [getDetailForCalculation, selectedSchedules, detailsState.filterDay],
    );

    useEffect(() => {
        if (!pack?.pack_contractor || !allRows.length) return;

        const dataTotal = {
            name: 'Total',
            total: 0,
            qty1: 0,
            qty2: 0,
            qty3: 0,
            people: 0,
        };

        const firstDetail = getDetailForCalculation();

        allRows.forEach(({ instanceId, item, date, calculated }) => {
            const rowDate = date || detailsState.filterDay;
            const detail = getDetailForDate(rowDate) || firstDetail;
            const rowCalculated = calculated || calculateItemDetails(item, detail, selectedSchedules);
            const editRow = editedRows[instanceId];
            const ratePerHour = editRow && editRow.ratePerHour !== undefined ? parseFloat(editRow.ratePerHour) || 0 : item.price;

            const baseShiftTimes = computeShiftStartEnd(detail, rowCalculated.array, item, rowDate);
            const shifts = [0, 1, 2].map((i) => {
                const idx = i + 1;
                const base = baseShiftTimes[i] || { start: '-', end: '-' };
                let start = base.start;
                let end = base.end;
                if (editRow && editRow.shifts && editRow.shifts[idx]) {
                    const e = editRow.shifts[idx];
                    if (e.start !== undefined) start = e.start;
                    if (e.end !== undefined) end = e.end;
                }
                const hours = start && end && start !== '-' && end !== '-' ? shiftHoursBetween(start, end) : rowCalculated.array[i]?.hours || 0;
                return { start, end, hours };
            });

            const qty1 = shifts[0].hours;
            const qty2 = shifts[1].hours;
            const qty3 = shifts[2].hours;
            const totalHours = qty1 + qty2 + qty3;
            const rowTotal = ratePerHour * totalHours;
            const people = shifts.filter((s) => s.hours > 0).length;

            dataTotal.total += rowTotal;
            dataTotal.qty1 += qty1;
            dataTotal.qty2 += qty2;
            dataTotal.qty3 += qty3;
            dataTotal.people += people;
        });

        setTotalData(dataTotal);
    }, [pack, allRows, selectedSchedules, editedRows, isEditMode, detailsState.filterDay, filterDays, getDetailForCalculation, getDetailForDate]);

    useEffect(() => {
        const el = scrollWrapperRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            setContainerWidth(el.clientWidth);
        });
        ro.observe(el);
        setContainerWidth(el.clientWidth);
        return () => ro.disconnect();
    }, [pack]);

    const handleDelete = () => {
        apiCall('packToVenueDelete', {
            ...detailsState,
            filterDay,
            filterDays,
            formattedDate,
            formattedDates,
            selectedSchedules,
        });
        saveAssigns();
        apiCall('form', false);
    };

    const enterEditMode = () => {
        setIsEditMode(true);
        setExpandedShift1(true);
        setExpandedShift2(true);
        setExpandedShift3(true);
    };

    const exitEditMode = () => {
        setIsEditMode(false);
        setExpandedShift1(false);
        setExpandedShift2(false);
        setExpandedShift3(false);
    };

    const [isResetting, setIsResetting] = useState(false);

    const handleResetAllShifts = () => {
        // Confirm before resetting all shift times (existing reset button warning)
        // eslint-disable-next-line no-alert
        const confirmed = window.confirm(
            $t('Reset all shift start and end times to their original assignment values for this pack? Rate per hour values will be kept.'),
        );
        if (!confirmed) return;

        const resetUrl = typeof assignToVenuesResetUrlTemplate !== 'undefined' ? assignToVenuesResetUrlTemplate.replace('PACK_ID', packId) : null;

        if (resetUrl) {
            setIsResetting(true);
            fetch(resetUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': typeof csrfToken !== 'undefined' ? csrfToken : '',
                },
                body: JSON.stringify({ venue_id: venueId }),
            })
                .then((res) => {
                    if (!res.ok) {
                        // A locked line refuses the reset, saying why.
                        return res
                            .json()
                            .catch(() => ({}))
                            .then((body) => {
                                throw { refusal: body?.error };
                            });
                    }
                    return res.json();
                })
                .then(() => {
                    apiCall('refetch');
                    apiCall('form', false);
                })
                .catch((error) => {
                    // eslint-disable-next-line no-alert
                    window.alert(error?.refusal ?? $t('Failed to reset customizations. Please try again.'));
                })
                .finally(() => setIsResetting(false));
            return;
        }

        // Client-only: clear shift overrides, keep rate per hour
        setEditedRows((prev) => {
            const next = {};
            allRows.forEach(({ instanceId }) => {
                const prevRow = prev?.[instanceId];
                if (prevRow?.ratePerHour !== undefined) {
                    next[instanceId] = { ratePerHour: prevRow.ratePerHour };
                }
            });
            return next;
        });
    };

    const handleSubmit = () => {
        const firstDetail = getDetailForCalculation();
        let duration = 0;
        let packScheduleStart = undefined;
        let packScheduleEnd = undefined;
        const hasManualTime = !!(startTime && endTime);

        if (hasManualTime) {
            packScheduleStart = moment(detailsState.filterDay + ' ' + startTime);
            packScheduleEnd = moment(detailsState.filterDay + ' ' + endTime);
            duration = timeToFloat(moment.utc(packScheduleEnd.diff(packScheduleStart)).format('HH:mm'));
        } else if (checked && selectedSchedules) {
            const sched = _.isArray(selectedSchedules) && selectedSchedules.length > 0 ? selectedSchedules[0] : selectedSchedules;
            duration = sched ? timeToFloat(sched.duration) : 0;
        } else if (firstDetail && 'duration' in firstDetail) {
            duration = firstDetail.duration;
        }

        const durationJson = {
            duration,
            useSessionTime: checked,
            start: packScheduleStart || (firstDetail && firstDetail.start),
            end: packScheduleEnd || (firstDetail && firstDetail.end),
        };
        const manualDurationJson = {
            duration: hasManualTime ? duration : (firstDetail && firstDetail.duration) || 0,
            useSessionTime: checked,
            start: packScheduleStart || (firstDetail && firstDetail.start),
            end: packScheduleEnd || (firstDetail && firstDetail.end),
        };
        const durationObject = { manual: manualDurationJson, default: durationJson };
        const customizationsByDate = {};
        filterDays.forEach((day, detailIndex) => {
            const rowsForDate = filterDays.length === 1 ? allRows : allRows.filter((r) => r.date === day);
            const detailForDate = allDetails?.[detailIndex] || getDetailForDate(day) || firstDetail;

            customizationsByDate[day] = {
                items: rowsForDate.map(({ instanceId, item, isOriginal }, idx) => {
                    const rowEdit = editedRows[instanceId];
                    const calculated = calculateItemDetails(item, detailForDate, selectedSchedules);
                    const baseShiftArray = computeShiftStartEnd(detailForDate, calculated.array, item, day);
                    const baseShiftMap = normalizeShiftMap({
                        1: baseShiftArray[0] || { start: '-', end: '-' },
                        2: baseShiftArray[1] || { start: '-', end: '-' },
                        3: baseShiftArray[2] || { start: '-', end: '-' },
                    });
                    const mergedShifts = mergeShiftMaps(baseShiftMap, rowEdit?.shifts || {});
                    const mergedDuration = durationFromShiftMap(mergedShifts);
                    const fallbackDuration = durationFromCalculatedArray(calculated);
                    return {
                        instance_id: String(idx + 1),
                        item_id: item.id,
                        is_original: isOriginal,
                        rate_per_hour: rowEdit?.ratePerHour !== undefined ? parseFloat(rowEdit.ratePerHour) || 0 : item.price,
                        duration: mergedDuration > 0 ? mergedDuration : fallbackDuration,
                        shifts: mergedShifts,
                    };
                }),
            };
        });

        const det = {
            ...detailsState,
            amount: detailsState.amount,
            selectedSchedules,
            checked,
            customizations: customizationsByDate,
            details: { ...firstDetail, useSessionTime: checked, start: durationJson.start, end: durationJson.end },
        };
        apiCall('packToVenueEdit', det, durationObject);
        saveAssigns();
        apiCall('form', false);
    };

    const removeRow = (instanceId) => {
        setAllRows((prev) => prev.filter((r) => r.instanceId !== instanceId));
        setEditedRows((prev) => {
            const next = { ...prev };
            delete next[instanceId];
            return next;
        });
    };

    const firstDetail = getDetailForCalculation();

    if (!pack) return null;

    return (
        <div>
            <div className="form-group mt-2">
                {(!checked || !selectedSchedules?.length) && (
                    <>
                        <div className="row no-gutters align-items-center mt-2">
                            <div className="col-3">
                                <label className="form-check-label">Start time</label>
                            </div>
                            <div className="col-9">
                                <input value={startTime} readOnly type="time" className="form-control form-control-sm" />
                            </div>
                        </div>
                        <div className="row no-gutters align-items-center mt-2">
                            <div className="col-3">
                                <label className="form-check-label">End time</label>
                            </div>
                            <div className="col-9">
                                <input value={endTime} readOnly type="time" className="form-control form-control-sm" />
                            </div>
                        </div>
                    </>
                )}
                {selectedSchedules?.length > 0 && selectedSchedules[0]?.start && (
                    <div className="row no-gutters align-items-center mt-2">
                        <div className="col-auto mr-2">
                            <div className="toggl-input">
                                <input checked={checked} id="edit-use-session" className="toggl-input-checkbox" type="checkbox" disabled />
                                <label htmlFor="edit-use-session" className="toggl-input-label mb-0" />
                            </div>
                        </div>
                        <div className="col">
                            <label htmlFor="edit-use-session" className="form-check-label">
                                {$t('Use session times')}
                            </label>
                        </div>
                    </div>
                )}
            </div>

            <div className="tw-flex tw-justify-end tw-items-center tw-gap-2 tw-mt-4">
                <Button variant="secondary" size="sm" onClick={handleResetAllShifts} disabled={isResetting}>
                    {isResetting ? $t('Resetting...') : $t('Reset')}
                </Button>
                {isEditMode ? (
                    <Button variant="secondary" size="sm" onClick={exitEditMode}>
                        {$t('Cancel')}
                    </Button>
                ) : (
                    <Button variant="primary" size="sm" onClick={enterEditMode}>
                        {$t('Edit')}
                    </Button>
                )}
            </div>

            <div ref={scrollWrapperRef} className="tw-mt-2 tw-overflow-x-auto">
                <div
                    className="tw-space-y-0 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-overflow-hidden"
                    style={getTableWidthStyle(
                        expandedShift1 || isEditMode,
                        expandedShift2 || isEditMode,
                        expandedShift3 || isEditMode,
                        containerWidth,
                        isEditMode,
                    )}
                >
                    <EditTableHeader
                        expandedShift1={expandedShift1 || isEditMode}
                        setExpandedShift1={isEditMode ? () => {} : setExpandedShift1}
                        expandedShift2={expandedShift2 || isEditMode}
                        setExpandedShift2={isEditMode ? () => {} : setExpandedShift2}
                        expandedShift3={expandedShift3 || isEditMode}
                        setExpandedShift3={isEditMode ? () => {} : setExpandedShift3}
                        containerWidth={containerWidth}
                        isEditMode={isEditMode}
                    />

                    {allRows.map(({ instanceId, item, date, calculated }) => (
                        <EditTableRow
                            key={instanceId}
                            rowKey={instanceId}
                            item={item}
                            calculated={calculated}
                            detail={getDetailForDate(date) || firstDetail}
                            dateForDetail={date || detailsState.filterDay}
                            onRemove={allRows.length > 1 ? () => removeRow(instanceId) : undefined}
                            isEditMode={isEditMode}
                            editedRow={editedRows[instanceId]}
                            onChangeEditRow={(updater) =>
                                setEditedRows((prev) => ({
                                    ...prev,
                                    [instanceId]: { ...(prev[instanceId] || {}), ...updater(prev[instanceId] || {}) },
                                }))
                            }
                            expandedShift1={expandedShift1 || isEditMode}
                            expandedShift2={expandedShift2 || isEditMode}
                            expandedShift3={expandedShift3 || isEditMode}
                            containerWidth={containerWidth}
                        />
                    ))}

                    <AddItemRow
                        dropdownOptions={dropdownOptions}
                        onSelect={addRowForItem}
                        expandedShift1={expandedShift1 || isEditMode}
                        expandedShift2={expandedShift2 || isEditMode}
                        expandedShift3={expandedShift3 || isEditMode}
                        containerWidth={containerWidth}
                        isEditMode={isEditMode}
                    />

                    <TotalsRow
                        totalData={totalData}
                        expandedShift1={expandedShift1 || isEditMode}
                        expandedShift2={expandedShift2 || isEditMode}
                        expandedShift3={expandedShift3 || isEditMode}
                        containerWidth={containerWidth}
                        isEditMode={isEditMode}
                    />
                </div>
            </div>

            <div className="modal__footer tw-mt-4">
                <Button variant="secondary" onClick={() => apiCall('form', false)}>
                    {$t('Cancel')}
                </Button>
                <Button
                    variant="danger"
                    className={pack?.commitment_exists ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}
                    onClick={() => {
                        if (pack?.commitment_exists) {
                            alert('You must delete the committed item attached to this cost pack allocation before it can be deleted');
                            return;
                        }
                        handleDelete();
                    }}
                >
                    {$t('Delete')}
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    {$t('Update')}
                </Button>
            </div>
        </div>
    );
}

function EditTableHeader({
    expandedShift1,
    setExpandedShift1,
    expandedShift2,
    setExpandedShift2,
    expandedShift3,
    setExpandedShift3,
    containerWidth,
    isEditMode,
}) {
    return (
        <div
            className="tw-grid tw-gap-4 tw-text-sm tw-font-semibold tw-text-center tw-items-center tw-bg-blue-50 tw-border-b tw-border-gray-300 [&>div]:tw-border-r [&>div]:tw-border-gray-200 [&>div]:tw-pr-3 [&>div]:tw-py-2.5 [&>div:first-child]:tw-pl-3 [&>div:last-child]:tw-border-r-0 [&>div:last-child]:tw-pr-0"
            style={getGridStyle(expandedShift1, expandedShift2, expandedShift3, containerWidth, isEditMode)}
        >
            <div className="tw-text-left">{$t('Description')}</div>
            <div>{$t('Rate per hour')}</div>
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                <span>{$t('Qty Shift 1')}</span>
                <button
                    type="button"
                    className="tw-p-0 tw-border-0 tw-bg-transparent tw-cursor-pointer"
                    onClick={() => setExpandedShift1((v) => !v)}
                    aria-label={expandedShift1 ? 'Collapse' : 'Expand'}
                >
                    {expandedShift1 ? <ChevronUpIcon className="tw-w-4 tw-h-4" /> : <ChevronDownIcon className="tw-w-4 tw-h-4" />}
                </button>
            </div>
            {expandedShift1 && (
                <>
                    <div>{$t('Start')}</div>
                    <div>{$t('End')}</div>
                </>
            )}
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                <span>{$t('Qty Shift 2')}</span>
                <button
                    type="button"
                    className="tw-p-0 tw-border-0 tw-bg-transparent tw-cursor-pointer"
                    onClick={() => setExpandedShift2((v) => !v)}
                    aria-label={expandedShift2 ? 'Collapse' : 'Expand'}
                >
                    {expandedShift2 ? <ChevronUpIcon className="tw-w-4 tw-h-4" /> : <ChevronDownIcon className="tw-w-4 tw-h-4" />}
                </button>
            </div>
            {expandedShift2 && (
                <>
                    <div>{$t('Start')}</div>
                    <div>{$t('End')}</div>
                </>
            )}
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                <span>{$t('Qty Shift 3')}</span>
                <button
                    type="button"
                    className="tw-p-0 tw-border-0 tw-bg-transparent tw-cursor-pointer"
                    onClick={() => setExpandedShift3((v) => !v)}
                    aria-label={expandedShift3 ? 'Collapse' : 'Expand'}
                >
                    {expandedShift3 ? <ChevronUpIcon className="tw-w-4 tw-h-4" /> : <ChevronDownIcon className="tw-w-4 tw-h-4" />}
                </button>
            </div>
            {expandedShift3 && (
                <>
                    <div>{$t('Start')}</div>
                    <div>{$t('End')}</div>
                </>
            )}
            <div>{$t('People')}</div>
            <div>{$t('Total')}</div>
            <div></div>
        </div>
    );
}

function EditTableRow({
    rowKey,
    item,
    calculated,
    detail,
    expandedShift1,
    expandedShift2,
    expandedShift3,
    onRemove,
    containerWidth,
    dateForDetail,
    isEditMode,
    editedRow,
    onChangeEditRow,
}) {
    const amount = 1;
    const baseShiftTimes = useMemo(
        () => computeShiftStartEnd(detail, calculated.array, item, dateForDetail),
        [detail, calculated, item.shift_overlap, dateForDetail],
    );

    const currentRate = editedRow && editedRow.ratePerHour !== undefined ? editedRow.ratePerHour : item.price;
    const shifts = useMemo(() => {
        const result = [0, 1, 2].map((i) => {
            const idx = i + 1;
            const base = baseShiftTimes[i] || { start: '-', end: '-' };
            let start = base.start;
            let end = base.end;
            if (editedRow && editedRow.shifts && editedRow.shifts[idx]) {
                const e = editedRow.shifts[idx];
                if (e.start !== undefined) start = e.start;
                if (e.end !== undefined) end = e.end;
            }
            const hours = start && end && start !== '-' && end !== '-' ? shiftHoursBetween(start, end) : 0;
            return { start, end, hours };
        });
        return result;
    }, [baseShiftTimes, editedRow, calculated]);

    const qty1 = shifts[0].hours * amount;
    const qty2 = shifts[1].hours * amount;
    const qty3 = shifts[2].hours * amount;
    const totalHours = (shifts[0].hours + shifts[1].hours + shifts[2].hours) * amount;
    const rowTotal = (parseFloat(currentRate) || 0) * totalHours;
    const people = shifts.filter((s) => s.hours > 0).length;

    let warning = null;
    let baseTotalHours = 0;
    calculated.array.forEach((element, i) => {
        const base = baseShiftTimes[i] || { start: '-', end: '-' };
        let start = base.start;
        let end = base.end;
        baseTotalHours += start && end && start !== '-' && end !== '-' ? shiftHoursBetween(start, end) : calculated.array[i]?.hours || 0;
    });

    if (totalHours > baseTotalHours) {
        warning = $t('Total shift hours for this row exceed the session-based duration.');
    } else if (totalHours < baseTotalHours) {
        warning = $t('Total shift hours for this row are below the session-based duration.');
    }

    const handleRateChange = (e) => {
        const value = e.target.value;
        if (!isEditMode) return;
        onChangeEditRow?.((prev) => ({
            ...prev,
            ratePerHour: value,
        }));
    };

    const handleTimeChange = (shiftIndex, field, value) => {
        if (!isEditMode) return;
        const idx = shiftIndex + 1;
        onChangeEditRow?.((prev) => ({
            ...prev,
            shifts: {
                ...(prev?.shifts || {}),
                [idx]: {
                    ...(prev?.shifts?.[idx] || {}),
                    [field]: value,
                },
            },
        }));
    };

    const renderTimeCell = (shiftIndex, field, expanded) => {
        if (!expanded) return null;
        const shift = shifts[shiftIndex];
        const value = shift[field] === '-' ? '' : shift[field];
        if (!isEditMode) {
            return <div className="tw-text-right">{shift[field] ?? '-'}</div>;
        }
        // Override the row's pr-3 cell padding so the full column width is available for the
        // native time input, which renders its own clock/spinner button on the right side.
        return (
            <div className="!tw-pr-0">
                <input
                    type="time"
                    className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-pl-2 tw-py-1 tw-text-sm"
                    value={value}
                    onChange={(e) => handleTimeChange(shiftIndex, field, e.target.value || '-')}
                />
            </div>
        );
    };

    return (
        <div
            className="tw-grid tw-gap-4 tw-text-sm tw-items-center tw-border-b tw-border-gray-200 tw-bg-white hover:tw-bg-gray-50 [&>div]:tw-border-r [&>div]:tw-border-gray-200 [&>div]:tw-pr-3 [&>div]:tw-py-2 [&>div:first-child]:tw-pl-3 [&>div:last-child]:tw-border-r-0 [&>div:last-child]:tw-pr-0"
            style={getGridStyle(expandedShift1, expandedShift2, expandedShift3, containerWidth, isEditMode)}
        >
            <div className="tw-col-span-1 tw-text-left tw-flex tw-items-center tw-gap-2">
                {item.name}
                {warning && (
                    <Tooltip placement="top-end" tooltipContent={warning} tooltipType="text" backgroundClass="tw-bg-charcoal">
                        <div>
                            <CircleInfoIcon className="tw-w-4 tw-h-4 tw-text-red-500" />
                        </div>
                    </Tooltip>
                )}
            </div>
            <div className="tw-text-right inbud-numeric-cell">
                {isEditMode ? (
                    <input
                        type="number"
                        step="0.01"
                        className="tw-w-full tw-text-right tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm"
                        value={currentRate}
                        onChange={handleRateChange}
                    />
                ) : (
                    // In view mode, show the effective rate for this row:
                    // customized rate_per_hour when present, otherwise the item price.
                    currentRate
                )}
            </div>
            {/* Shift 1 */}
            <div className="tw-text-right inbud-numeric-cell">{qty1 ? qty1.toFixed(1) : '-'}</div>
            {expandedShift1 && (
                <>
                    {renderTimeCell(0, 'start', true)}
                    {renderTimeCell(0, 'end', true)}
                </>
            )}
            {/* Shift 2 */}
            <div className="tw-text-right inbud-numeric-cell">{qty2 ? qty2.toFixed(1) : '-'}</div>
            {expandedShift2 && (
                <>
                    {renderTimeCell(1, 'start', true)}
                    {renderTimeCell(1, 'end', true)}
                </>
            )}
            {/* Shift 3 */}
            <div className="tw-text-right inbud-numeric-cell">{qty3 ? qty3.toFixed(1) : '-'}</div>
            {expandedShift3 && (
                <>
                    {renderTimeCell(2, 'start', true)}
                    {renderTimeCell(2, 'end', true)}
                </>
            )}
            <div className="tw-text-right inbud-numeric-cell">{people || people === 0 ? people : '-'}</div>
            <div className="tw-text-right inbud-numeric-cell">{rowTotal ? rowTotal.toFixed(1) : '-'}</div>
            <div className="tw-text-right inbud-numeric-cell">
                {onRemove && (
                    <button
                        type="button"
                        className="tw-p-1 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-red-500 hover:tw-text-red-700"
                        onClick={onRemove}
                        aria-label={$t('Remove')}
                    >
                        <TrashIcon className="tw-w-4 tw-h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}

function AddItemRow({ dropdownOptions, onSelect, expandedShift1, expandedShift2, expandedShift3, containerWidth, isEditMode }) {
    const [value, setValue] = useState('');

    const handleChange = (e) => {
        const id = e.target.value;
        if (!id) return;
        const item = dropdownOptions.find((o) => o.id == id);
        if (item) {
            onSelect(item);
            setValue('');
        }
    };

    return (
        <div
            className="tw-grid tw-gap-4 tw-text-sm tw-items-center tw-border-b tw-border-gray-200 tw-bg-white [&>div]:tw-border-r [&>div]:tw-border-gray-200 [&>div]:tw-pr-3 [&>div]:tw-py-2 [&>div:first-child]:tw-pl-3 [&>div:last-child]:tw-border-r-0 [&>div:last-child]:tw-pr-0"
            style={getGridStyle(expandedShift1, expandedShift2, expandedShift3, containerWidth, isEditMode)}
        >
            <div className="tw-col-span-1">
                <select value={value} onChange={handleChange} className="form-control form-control-sm" aria-label={$t('Add pack items')}>
                    <option value="">+ {$t('Add pack items')}...</option>
                    {dropdownOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                            {opt.name}
                        </option>
                    ))}
                </select>
            </div>
            <div className="inbud-numeric-cell">-</div>
            <div className="inbud-numeric-cell">-</div>
            {expandedShift1 && (
                <>
                    <div></div>
                    <div></div>
                </>
            )}
            <div className="inbud-numeric-cell">-</div>
            {expandedShift2 && (
                <>
                    <div></div>
                    <div></div>
                </>
            )}
            <div className="inbud-numeric-cell">-</div>
            {expandedShift3 && (
                <>
                    <div></div>
                    <div></div>
                </>
            )}
            <div className="inbud-numeric-cell">-</div>
            <div className="inbud-numeric-cell">-</div>
            <div></div>
        </div>
    );
}

function TotalsRow({ totalData, expandedShift1, expandedShift2, expandedShift3, containerWidth, isEditMode }) {
    return (
        <div
            className="tw-grid tw-gap-4 tw-text-sm tw-font-semibold tw-items-center tw-bg-gray-100 tw-border-t-2 tw-border-gray-300 [&>div]:tw-border-r [&>div]:tw-border-gray-200 [&>div]:tw-pr-3 [&>div]:tw-py-2.5 [&>div:first-child]:tw-pl-3 [&>div:last-child]:tw-border-r-0 [&>div:last-child]:tw-pr-0"
            style={getGridStyle(expandedShift1, expandedShift2, expandedShift3, containerWidth, isEditMode)}
        >
            <div className="tw-col-span-1">{totalData.name}</div>
            <div></div>
            <div className="inbud-numeric-cell tw-text-right">{Math.round(totalData.qty1)}</div>
            {expandedShift1 && (
                <>
                    <div></div>
                    <div></div>
                </>
            )}
            <div className="inbud-numeric-cell tw-text-right">{totalData.qty2 ? Math.round(totalData.qty2) : '-'}</div>
            {expandedShift2 && (
                <>
                    <div></div>
                    <div></div>
                </>
            )}
            <div className="inbud-numeric-cell tw-text-right">{totalData.qty3 ? Math.round(totalData.qty3) : '-'}</div>
            {expandedShift3 && (
                <>
                    <div></div>
                    <div></div>
                </>
            )}
            <div className="inbud-numeric-cell tw-text-right">{totalData.people}</div>
            <div className="inbud-numeric-cell tw-text-right">{Math.round(totalData.total)}</div>
            <div></div>
        </div>
    );
}
